import './AppShowcase.css';

class AppShowcase {
  constructor(container, appData = {}) {
    this.container = container;
    this.appData = appData;
  }

  async render() {
    try {
      // Create a page that matches the existing GreenCheck structure but with dynamic data
      this.container.innerHTML = `
        <div class="greencheck-page">
          <div class="greencheck-container">
            ${this.renderAppHeader()}
            ${this.renderScreenshotGallery()}
          </div>
        </div>
      `;
      
      this.setupEventListeners();
      
      // Add a subtle load animation
      this.container.style.opacity = '0';
      this.container.style.transform = 'translateY(10px)';
      this.container.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
      
      setTimeout(() => {
        this.container.style.opacity = '1';
        this.container.style.transform = 'translateY(0)';
      }, 50);
      
    } catch (error) {
      console.error('Error rendering app page:', error);
      this.container.innerHTML = '<p>Error loading content. Please refresh the page.</p>';
    }
  }

  renderAppHeader() {
    const { appIconUrl, title, subtitle, websiteUrl, appStoreUrl, rating } = this.appData;
    
    // Generate star rating
    const renderStars = (count) => {
      return Array(count).fill(0).map(() => 
        `<img src="/assets/apps/Star.svg" alt="Star" class="star-icon" width="16" height="16" />`
      ).join('');
    };
    
    return `
      <div class="app-header">
        <div class="app-header-main">
          <div class="app-icon-container">
            <div class="app-icon">
              ${appIconUrl ? 
                `<img src="${appIconUrl}" alt="${title} App Icon" class="app-icon-image" />` :
                `<div class="app-icon-background">
                  <div class="app-icon-badge">
                    <span class="checkmark">✓</span>
                  </div>
                </div>`
              }
            </div>
          </div>
          
          <div class="app-info">
            <h1 class="app-title">${title || 'App Title'}</h1>
            
            <p class="app-subtitle">${subtitle || 'App Subtitle'}</p>
            
            ${websiteUrl ? 
              `<a href="${websiteUrl}" target="_blank" rel="noopener noreferrer" class="browse-website-link">
                Browse Website
              </a>` : 
              ''
            }
            
            <div class="rating-section">
              <div class="stars">
                ${renderStars(rating || 5)}
              </div>
            </div>
            
            ${appStoreUrl ?
              `<a href="${appStoreUrl}" target="_blank" rel="noopener noreferrer" class="download-button">
                <img src="/assets/apps/Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg" alt="Download on the App Store" class="app-store-badge" height="40" />
              </a>` :
              ''
            }
          </div>
        </div>
      </div>
    `;
  }

  renderScreenshotGallery() {
    const { screenshotUrls, screenshotData, content } = this.appData;
    
    // Use screenshotData if available, otherwise create default screenshots
    const screenshots = screenshotData && screenshotData.length > 0 
      ? screenshotData.map((data, index) => ({
          id: index + 1,
          title: data.title,
          description: data.description,
          alt: data.alt || data.title,
          imageUrl: screenshotUrls && screenshotUrls[index] ? screenshotUrls[index] : null
        }))
      : [
          {
            id: 1,
            title: "Feature 1",
            description: "Main app functionality",
            imageUrl: screenshotUrls && screenshotUrls[0] ? screenshotUrls[0] : null
          },
          {
            id: 2,
            title: "Feature 2", 
            description: "Additional capabilities",
            imageUrl: screenshotUrls && screenshotUrls[1] ? screenshotUrls[1] : null
          },
          {
            id: 3,
            title: "Feature 3",
            description: "Advanced features",
            imageUrl: screenshotUrls && screenshotUrls[2] ? screenshotUrls[2] : null
          },
          {
            id: 4,
            title: "Feature 4",
            description: "User experience",
            imageUrl: screenshotUrls && screenshotUrls[3] ? screenshotUrls[3] : null
          }
        ];

    return `
      <section class="screenshots-section">
        <!-- App description from MDX content -->
        <div class="app-description">
          ${content ? this.renderMarkdownContent(content) : ''}
        </div>
        
        <h2 class="screenshots-title">iPhone Screenshots</h2>
        
        <div class="screenshots-container">
          <div class="screenshots-gallery">
            <div class="screenshots-track" data-current-index="0">
              ${screenshots.map((screenshot, index) => `
                <div class="screenshot-item ${index === 0 ? 'active' : ''}" data-index="${index}">
                  <div class="screenshot-frame">
                    ${screenshot.imageUrl ?
                      `<img src="${screenshot.imageUrl}" alt="${screenshot.alt || screenshot.title}" class="screenshot-image" />` :
                      `<div class="screenshot-placeholder">
                        <div class="screenshot-content">
                          <h3 class="screenshot-title">${screenshot.title}</h3>
                          <p class="screenshot-description">${screenshot.description}</p>
                        </div>
                      </div>`
                    }
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      </section>
    `;
  }

  renderMarkdownContent(content) {
    // Simple markdown-to-HTML conversion for basic formatting
    return content
      .replace(/^# (.+)$/gm, '<h3 class="description-title">$1</h3>')
      .replace(/^## (.+)$/gm, '<h3 class="solution-title">$1</h3>')
      .replace(/^\*\*(.+?)\*\*/gm, '<strong>$1</strong>')
      .replace(/^\*(.+)$/gm, '<p><span class="feature-icon">✨</span> $1</p>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/^(.+)$/gm, '<p>$1</p>')
      .replace(/<p><h/g, '<h')
      .replace(/<\/h3><\/p>/g, '</h3>')
      .replace(/<p><p>/g, '<p>')
      .replace(/<\/p><\/p>/g, '</p>')
      .replace(/<p><span class="feature-icon">✨<\/span>/g, '<div class="features-list"><p><span class="feature-icon">✨</span>')
      .replace(/(<p><span class="feature-icon">✨<\/span>.*?<\/p>)(?!\s*<p><span class="feature-icon">)/g, '$1</div>');
  }

  setupEventListeners() {
    const screenshotItems = this.container.querySelectorAll('.screenshot-item');

    let currentIndex = 0;

    const updateGallery = (newIndex) => {
      currentIndex = newIndex;
      
      // Update active states
      screenshotItems.forEach((item, index) => {
        item.classList.toggle('active', index === currentIndex);
      });
    };

    // Screenshot items
    screenshotItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        updateGallery(index);
      });
    });
  }
}

export default AppShowcase; 